import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { mensagemErroPasta } from "@/features/atticus-rateio/entrega/regras";
import { fetchPastaEscritorio, fetchQuadroEscritorio, moverPasta, type QuadroEscritorio } from "./api";

export const quadroEscritorioKey = (orgId: string | null | undefined) => ["pastas-escritorio-quadro", orgId] as const;
export const pastaEscritorioKey = (dealId: string | null | undefined) => ["pastas-escritorio-pasta", dealId] as const;

export function useQuadroEscritorio(orgId: string | null | undefined) {
  return useQuery({
    queryKey: quadroEscritorioKey(orgId),
    queryFn: () => fetchQuadroEscritorio(orgId!),
    enabled: !!orgId,
    staleTime: 30 * 1000,
  });
}

export function usePastaEscritorio(dealId: string | null | undefined) {
  return useQuery({
    queryKey: pastaEscritorioKey(dealId),
    queryFn: () => fetchPastaEscritorio(dealId!),
    enabled: !!dealId,
  });
}

/** Move com atualização otimista do quadro; erro do servidor volta o cartão e explica. */
export function useMoverPasta(orgId: string | null | undefined) {
  const qc = useQueryClient();
  const key = quadroEscritorioKey(orgId);
  return useMutation({
    mutationFn: ({ dealId, stageId }: { dealId: string; stageId: string }) => moverPasta(dealId, stageId),
    onMutate: async ({ dealId, stageId }) => {
      await qc.cancelQueries({ queryKey: key });
      const antes = qc.getQueryData<QuadroEscritorio | null>(key);
      if (antes) {
        qc.setQueryData<QuadroEscritorio>(key, {
          ...antes,
          pastas: antes.pastas.map((p) =>
            p.deal_id === dealId ? { ...p, stage_id: stageId, etapa_desde: new Date().toISOString() } : p,
          ),
        });
      }
      return { antes };
    },
    onError: (err, _vars, ctx) => {
      if (ctx?.antes !== undefined) qc.setQueryData(key, ctx.antes);
      toast.error(mensagemErroPasta(err) ?? "Não foi possível mover a pasta.");
    },
    onSettled: (_d, _e, { dealId }) => {
      qc.invalidateQueries({ queryKey: key });
      qc.invalidateQueries({ queryKey: pastaEscritorioKey(dealId) });
    },
  });
}
